import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { useState } from "react";
import { connect } from "react-redux";

export const enum fields {
    image = "image",
    name = "name",
    title = "title",
    link = "link",
    description = "description",

    displayImage = "Image",
    displayName = "Name",
    displayTitle = "Title",
    displayLink = "CTA Label",
    displayDescription = "Description",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.name]: string;
        [fields.title]: string;
        [fields.link]: string;
        [fields.description]: string;
    };
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

export type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface TeamCardWidgetStyles {
    wrapper?: InjectableCss;
    thumbnail?: InjectableCss;
    textWrapper?: InjectableCss;
    name?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    modal?: InjectableCss;
    modalTextWrapper?: InjectableCss;
    description?: TypographyPresentationProps;
}

export const teamCardWidgetStyles: TeamCardWidgetStyles = {};

export const styles = teamCardWidgetStyles;

export const TeamCardWidget = ({ id, fields: { name, title, link, description, image } }: Props) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const modalCloseHandler = () => setIsModalOpen(false);
    const modalOpenHandler = () => {
        if (description) {
            setIsModalOpen(true);
        }
    };

    return (
        <>
            <StyledWrapper>
                <StyledWrapper
                    className={description ? "team-card__wrapper--with-hover" : ""}
                    {...styles.wrapper}
                    onClick={modalOpenHandler}
                >
                    <StyledWrapper {...styles.thumbnail}>
                        <LazyImage src={image} altText={`${name}, ${title}`} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.textWrapper}>
                        <Typography {...styles.name}>{name}</Typography>
                        <Typography {...styles.title}>{title}</Typography>
                        {link && <Link {...styles.link}>{link}</Link>}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>

            <Modal
                key={`teamCard-${id}`}
                headline={`${name}, ${title}`}
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
                {...styles.modal}
            >
                <StyledWrapper {...styles.modalTextWrapper}>
                    <StyledWrapper {...styles.description}>{parse(description, parserOptions)}</StyledWrapper>
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(TeamCardWidget) as any,
    definition: {
        displayName: "Team Card",
        group: "Verndale Box" as any,
        icon: "user-tie",
        fieldDefinitions: [
            {
                name: fields.image,
                displayName: fields.displayImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: "recommendedSize",
                displayName: "Use recommended size (360x200)",
                defaultValue: "",
                editorTemplate: "CustomLabelField" as any,
                fieldType: "Translatable",
            },
            {
                name: fields.name,
                displayName: fields.displayName,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.title,
                displayName: fields.displayTitle,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
            },
            {
                name: fields.link,
                displayName: fields.displayLink,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.description,
                displayName: fields.displayDescription,
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
